import * as yup from 'yup';
import {
  HostedFieldsHostedFieldsFieldData,
  HostedFieldsAccountDetails,
} from 'braintree-web/hosted-fields';

import {
  Customer,
  PaymentMethod,
  EPaymentProvider,
  BraintreeCardFinalizeRequestBody,
} from '../../../../contracts';
import i18n from '../../../../i18n';
import { SavedCardFormProps } from '../SavedCards/SavedCards';

export interface BraintreeCardForm {
  email: string;
  cardholderName: string;
  shouldSaveCard: boolean;
}
export const getCardFormSchema = (shouldHideEmailField?: boolean) => {
  return yup.object().shape({
    email: shouldHideEmailField
      ? yup.string().notRequired()
      : yup
          .string()
          .required(i18n.t('checkout.fieldErrors.incompleteEmail'))
          .email(i18n.t('checkout.fieldErrors.invalidEmail')),
    cardholderName: yup.string().required(i18n.t('checkout.fieldErrors.incompleteCardholderName')),
  });
};

const invalidCardFieldToErrorsMap: Record<string, string> = {
  number: i18n.t('checkout.fieldErrors.invalidCard'),
  expirationDate: i18n.t('checkout.fieldErrors.invalidExpirationDate'),
  cvv: i18n.t('checkout.fieldErrors.invalidSecurityCode'),
};

const incompleteCardFieldToErrorsMap: Record<string, string> = {
  number: i18n.t('checkout.fieldErrors.incompleteCard'),
  expirationDate: i18n.t('checkout.fieldErrors.incompleteExpirationDate'),
  cvv: i18n.t('checkout.fieldErrors.incompleteSecurityCode'),
};

export const getCardErrorMessage = (field: string, meta: HostedFieldsHostedFieldsFieldData) => {
  if (!meta.isValid) {
    return meta.isEmpty
      ? incompleteCardFieldToErrorsMap[field]
      : invalidCardFieldToErrorsMap[field];
  }
  return '';
};

export const getVerifyCardFormattedAmount = (amount: number) => {
  const formattedNumber = (amount / 100).toString();
  return formattedNumber;
};

export function createBraintreeCardFinalizeRequestBody(
  nonce: string,
  formValues: BraintreeCardForm,
  selectedPaymentMethod: PaymentMethod,
  cardDetails?: HostedFieldsAccountDetails
): BraintreeCardFinalizeRequestBody {
  return {
    paymentProvider: EPaymentProvider.braintree,
    receipt_email: formValues.email,
    paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
    paymentMethodId: selectedPaymentMethod?.id,
    params: {
      paymentNonce: nonce,
      cardHolderName: formValues.cardholderName,
      shouldSaveCard: formValues.shouldSaveCard,
      cardDetails: {
        bin: cardDetails?.bin || '',
        expirationMonth: cardDetails?.expirationMonth || '',
        expirationYear: cardDetails?.expirationYear || '',
        lastFourDigits: cardDetails?.lastFour || '',
        nameOnCard: formValues.cardholderName || '',
        cardType: cardDetails?.cardType || '',
      },
    },
  };
}

export function createBraintreeSavedCardFinalizeRequestBody(
  formValues: SavedCardFormProps,
  selectedPaymentMethod: PaymentMethod
): BraintreeCardFinalizeRequestBody {
  return {
    paymentProvider: EPaymentProvider.braintree,
    receipt_email: formValues.email,
    paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
    paymentMethodId: selectedPaymentMethod?.id,
    params: {
      paymentToken: formValues.cardID,
    },
  };
}

export function getBillingAddress(zipCode?: string, address?: string, customer?: Customer) {
  const zipCodeValue = zipCode || customer?.address?.zipCode;
  const addressValue = address || customer?.address?.address;
  return {
    ...(customer?.geoLocator?.countryCode2
      ? { countryCodeAlpha2: customer?.geoLocator?.countryCode2 }
      : {}),
    ...(zipCodeValue ? { postalCode: zipCodeValue } : {}),
    ...(addressValue ? { streetAddress: addressValue, extendedAddress: addressValue } : {}),
  };
}
